/**
 * iter-338k: BOQTaskDateInputs — Start/End date pickers under each BOQ row's
 * Task Progress slider. Bidirectional sync with Gantt SSOT:
 *
 *  - On mount, reads task dates from a shared map (passed via props).
 *  - On edit, calls POST /boq-worksheets/{id}/items/{item_id}/ensure-task to
 *    get-or-create the task, then PUT /tasks/{task_id} with start_date + end_date.
 *  - On save, calls onSaved(task_id, dates) so the parent can update the map +
 *    its own item.task_id.
 *
 *  Compact UI: two <input type="date"> fields side-by-side with a tiny Save
 *  button that lights up only when either date changes.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toISODate(v) {
  if (!v) return '';
  if (v.length >= 10 && v[4] === '-' && v[7] === '-') return v.slice(0, 10);
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch { return ''; }
}

export default function BOQTaskDateInputs({ worksheetId, item, taskDates, readOnly, onSaved }) {
  const taskId = item?.task_id || item?.source_task_id || null;
  const taskRec = taskId && taskDates ? taskDates[taskId] : null;
  const [start, setStart] = useState(toISODate(taskRec?.start_date));
  const [end, setEnd] = useState(toISODate(taskRec?.end_date));
  const [saving, setSaving] = useState(false);

  // Reset when the underlying task changes (e.g. another save refetched data)
  useEffect(() => {
    setStart(toISODate(taskRec?.start_date));
    setEnd(toISODate(taskRec?.end_date));
  }, [taskRec?.start_date, taskRec?.end_date]);

  const origStart = toISODate(taskRec?.start_date);
  const origEnd = toISODate(taskRec?.end_date);
  const dirty = (start !== origStart) || (end !== origEnd);

  const handleSave = async () => {
    if (!worksheetId || !item) return;
    if (start && end && start > end) {
      toast.error('Start date must be on/before end date');
      return;
    }
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    setSaving(true);
    try {
      // Step 1: ensure-task gives us a task_id (creates one if needed)
      const ensureBody = {
        title: item.task_title || item.description || 'BOQ Task',
        start_date: start || null,
        end_date: end || null,
      };
      const ensure = await axios.post(
        `${API}/boq-worksheets/${worksheetId}/items/${item.id || item._editId}/ensure-task`,
        ensureBody, { headers },
      );
      const tid = ensure.data?.task_id;
      if (!tid) throw new Error('No task_id returned');

      // Step 2: PUT to /tasks/{id} (also writes back through SSOT, syncs Gantt)
      const putBody = {};
      if (start) putBody.start_date = start;
      if (end) putBody.end_date = end;
      if (Object.keys(putBody).length > 0) {
        await axios.put(`${API}/tasks/${tid}`, putBody, { headers });
      }
      toast.success(start && end ? 'Task dates saved' : 'Task date saved');
      onSaved && onSaved(tid, { start_date: start, end_date: end });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save task dates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 mt-1 text-[11px] text-stone-600"
      data-testid={`boq-task-dates-${item?.id || item?._editId}`}
    >
      <CalendarDays size={11} className="text-stone-400 shrink-0" />
      <span className="text-stone-500">Start</span>
      <input
        type="date"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        disabled={readOnly || saving}
        className="h-6 px-1 border border-stone-200 rounded text-[11px] bg-white"
        data-testid={`boq-task-start-${item?.id || item?._editId}`}
      />
      <span className="text-stone-500">End</span>
      <input
        type="date"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        disabled={readOnly || saving}
        className="h-6 px-1 border border-stone-200 rounded text-[11px] bg-white"
        data-testid={`boq-task-end-${item?.id || item?._editId}`}
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={!dirty || readOnly || saving}
        className={`inline-flex items-center gap-0.5 h-6 px-1.5 rounded border text-[11px] ${dirty && !readOnly ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'}`}
        data-testid={`boq-task-dates-save-${item?.id || item?._editId}`}
        title={taskId ? 'Update Gantt task dates' : 'Create Gantt task with these dates'}
      >
        <Save size={10} /> Save
      </button>
      {!taskId && !start && !end && (
        <span className="text-stone-300 italic ml-1">— add dates to link to Gantt</span>
      )}
    </div>
  );
}
